import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const INTERESTS = [
  "art", "books", "ceramics", "cooking", "cycling", "design", "film", "gardening",
  "hiking", "literature", "matcha", "meditation", "natural wine", "photography",
  "poetry", "pottery", "running", "sound healing", "swimming", "tea ceremony",
  "thrifting", "travel", "vinyl", "yoga"
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    name: user?.name || "",
    age: user?.age || "",
    gender: user?.gender || "",
    pronouns: user?.pronouns || "",
    location: user?.location || "",
    bio: user?.bio || "",
    photo_url: user?.photo_url || user?.picture || "",
    interests: user?.interests || [],
    looking_for_dating: true,
    looking_for_fam: true,
  });
  const [saving, setSaving] = useState(false);

  const toggleInterest = (i) => {
    setData((d) => ({
      ...d,
      interests: d.interests.includes(i) ? d.interests.filter((x) => x !== i) : [...d.interests, i],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data, age: data.age ? parseInt(data.age, 10) : undefined };
      const { data: updated } = await api.put("/profile", payload);
      setUser(updated);
      toast.success("Profile saved.");
      navigate("/verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F5F5F5]">
      <Navbar />

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="flex justify-between items-center mb-10">
          <span className="font-label text-[0.6rem] text-[#D4AF37]">Step {step} of 3</span>
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`h-px w-10 ${i <= step ? "bg-[#D4AF37]" : "bg-[#D4AF37]/20"}`} />
            ))}
          </div>
        </div>

        <h1 className="font-display text-5xl sm:text-6xl font-normal" data-testid="onboarding-title">
          {step === 1 && <>Let us meet <span className="italic text-[#D4AF37]">you.</span></>}
          {step === 2 && <>What moves <span className="italic text-[#D4AF37]">you</span>?</>}
          {step === 3 && <>What are you <span className="italic text-[#D4AF37]">seeking</span>?</>}
        </h1>

        <div className="mt-12 goji-glass p-8 sm:p-10" data-testid="onboarding-form">
          {step === 1 && (
            <div className="space-y-6">
              <Input label="Name" value={data.name} onChange={(v) => setData({ ...data, name: v })} testId="onb-name" />
              <div className="grid grid-cols-2 gap-5">
                <Input label="Age" type="number" value={data.age} onChange={(v) => setData({ ...data, age: v })} testId="onb-age" />
                <Input label="City" value={data.location} onChange={(v) => setData({ ...data, location: v })} placeholder="Amsterdam" testId="onb-location" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <Input label="Gender" value={data.gender} onChange={(v) => setData({ ...data, gender: v })} placeholder="female / non-binary..." testId="onb-gender" />
                <Input label="Pronouns" value={data.pronouns} onChange={(v) => setData({ ...data, pronouns: v })} placeholder="she/her" testId="onb-pronouns" />
              </div>
              <Input label="Photograph URL" value={data.photo_url} onChange={(v) => setData({ ...data, photo_url: v })} placeholder="https://..." testId="onb-photo" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="font-label text-[0.6rem] text-[#D4AF37] mb-2 block">Introduction</label>
                <textarea
                  value={data.bio}
                  onChange={(e) => setData({ ...data, bio: e.target.value })}
                  rows={4}
                  placeholder="Tell us about yourself."
                  className="w-full px-4 py-3 text-sm"
                  data-testid="onb-bio"
                />
              </div>
              <div>
                <label className="font-label text-[0.6rem] text-[#D4AF37] mb-3 block">Affinities</label>
                <div className="flex flex-wrap gap-2" data-testid="onb-interests">
                  {INTERESTS.map((i) => (
                    <button
                      key={i}
                      onClick={() => toggleInterest(i)}
                      className={`px-4 py-2 font-label text-[0.6rem] transition-colors ${
                        data.interests.includes(i)
                          ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]"
                          : "border border-[#D4AF37]/25 text-[#F5F5F5]/70 hover:border-[#D4AF37]"
                      }`}
                      data-testid={`onb-interest-${i.replace(/\s+/g, "-")}`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-[#F5F5F5]/65 text-sm">Choose one or both. You may change this any time.</p>
              <button
                onClick={() => setData({ ...data, looking_for_dating: !data.looking_for_dating })}
                className={`w-full p-6 border text-left transition-colors ${
                  data.looking_for_dating ? "border-[#D4AF37] bg-[#D4AF37]/[0.04]" : "border-[#D4AF37]/25"
                }`}
                data-testid="onb-mode-dating"
              >
                <p className="font-display text-2xl">Romance</p>
                <p className="text-sm text-[#F5F5F5]/65 mt-1">Curated introductions for considered love.</p>
              </button>
              <button
                onClick={() => setData({ ...data, looking_for_fam: !data.looking_for_fam })}
                className={`w-full p-6 border text-left transition-colors ${
                  data.looking_for_fam ? "border-[#D4AF37] bg-[#D4AF37]/[0.04]" : "border-[#D4AF37]/25"
                }`}
                data-testid="onb-mode-fam"
              >
                <p className="font-display text-2xl">Chosen Family</p>
                <p className="text-sm text-[#F5F5F5]/65 mt-1">Real friendship grounded in shared values.</p>
              </button>
            </div>
          )}

          <div className="mt-10 flex justify-between">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="onb-back-button">Back</button>
            ) : <div />}
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} className="btn-ghost flex items-center gap-2" data-testid="onb-next-button">
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button onClick={save} disabled={saving} className="btn-ghost flex items-center gap-2" data-testid="onb-finish-button">
                {saving ? "Saving..." : "To verification"}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "", testId }) {
  return (
    <div>
      <label className="font-label text-[0.6rem] text-[#D4AF37] mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
